import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useCallback } from 'react'
import { FieldHookConfig } from 'formik'
import { useField } from '@/hooks/useField'

import ErrorMessage from '@/components/formik/messages/ErrorMessage'
import DatePickerInput, { DatePickerInputProps } from '@/components/ui/inputs/DatePickerInput'

dayjs.extend(utc)

type DatePickerFieldProps = Omit<DatePickerInputProps, 'onChange' | 'value'> &
  FieldHookConfig<Date | null> & {
    forceUtc?: boolean
  }

const MemoizedDatePickerInput = React.memo(DatePickerInput)

export default function DatePickerField(props: DatePickerFieldProps) {
  const { disabled, name, readOnly, forceUtc = false, ...rest } = props
  const [field, meta, helpers] = useField<Dayjs | null>(name)

  const handleChange = useCallback(
    (newValue: Dayjs | null, inputValue?: string) => {
      if (forceUtc) {
        // Preserve the local date values (year, month, day) and create a UTC date
        // This prevents timezone shifts for date-only values like birthdays
        if (newValue) {
          const utcDate = dayjs.utc().year(newValue.year()).month(newValue.month()).date(newValue.date()).startOf('day')
          helpers.setValue(utcDate)
        } else {
          helpers.setValue(null)
        }
      } else {
        // Convert to UTC but preserve the moment in time
        helpers.setValue(newValue ? newValue.utc() : null)
      }
    },
    [helpers, forceUtc]
  )

  return (
    <MemoizedDatePickerInput
      {...rest}
      disabled={disabled}
      error={!!meta.error && meta.touched && !disabled && !readOnly}
      helperText={!!meta.error && meta.touched && <ErrorMessage error={meta.error} />}
      name={field.name}
      onBlur={field.onBlur}
      onChange={handleChange}
      readOnly={readOnly}
      value={meta.value ? (forceUtc ? dayjs.utc(meta.value) : meta.value) : null}
    />
  )
}
